import { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plane, MapPin, Calendar, CheckCircle2, Download, Share2, Copy, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

interface PackingItem {
  id: string;
  name: string;
  category: string;
  essential: boolean;
  quantity?: number;
  packed: boolean;
}

interface ShareablePackingCardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  destination: string;
  country: string;
  tripType: string;
  tripLength: number;
  startDate?: Date;
  items: PackingItem[];
  packedPercentage: number;
}

export function ShareablePackingCard({
  open,
  onOpenChange,
  destination,
  country,
  tripType,
  tripLength,
  startDate,
  items,
  packedPercentage
}: ShareablePackingCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const totalItems = items.length;
  const packedItems = items.filter(i => i.packed).length;
  const essentialItems = items.filter(i => i.essential).length;
  const packedEssentials = items.filter(i => i.essential && i.packed).length;

  const getThemeColors = () => {
    const themes: Record<string, { gradient: string; accent: string; ring: string; hex: string }> = {
      business: { gradient: 'from-slate-600 to-blue-700', accent: 'bg-cyan-600', ring: 'text-cyan-600', hex: '#2563eb' },
      leisure: { gradient: 'from-emerald-500 to-teal-600', accent: 'bg-teal-500', ring: 'text-teal-500', hex: '#14b8a6' },
      adventure: { gradient: 'from-cyan-500 to-red-600', accent: 'bg-orange-500', ring: 'text-orange-500', hex: '#f97316' },
      family: { gradient: 'from-cyan-500 to-teal-500', accent: 'bg-cyan-500', ring: 'text-cyan-500', hex: '#a855f7' },
      romantic: { gradient: 'from-cyan-500 to-teal-500', accent: 'bg-cyan-500', ring: 'text-cyan-500', hex: '#f43f5e' },
      solo: { gradient: 'from-cyan-500 to-teal-500', accent: 'bg-cyan-500', ring: 'text-cyan-500', hex: '#6366f1' },
      beach: { gradient: 'from-cyan-400 to-teal-600', accent: 'bg-cyan-500', ring: 'text-cyan-500', hex: '#06b6d4' },
      city: { gradient: 'from-zinc-600 to-neutral-800', accent: 'bg-muted', ring: 'text-muted-foreground', hex: '#52525b' },
      cultural: { gradient: 'from-amber-500 to-teal-500', accent: 'bg-amber-500', ring: 'text-amber-500', hex: '#f59e0b' }
    };
    return themes[tripType.toLowerCase()] || themes.leisure;
  };

  const theme = getThemeColors();

  // Helper to darken/lighten hex color
  const adjustColor = (hex: string, amount: number): string => {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.min(255, Math.max(0, (num >> 16) + amount));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
    const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
    return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`;
  };

  const formatDate = (date?: Date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const generateShareText = () => {
    const emoji = packedPercentage === 100 ? '✅' : '🧳';
    return `${emoji} Ready for ${destination}! ${packedItems}/${totalItems} items packed (${Math.round(packedPercentage)}%)

Planning my ${tripLength}-day ${tripType} trip and already crushing my packing list! 

Pack smarter with @OdyVoy ✈️
https://odyvoy.com

#TravelReady #PackingList #${destination.replace(/\s+/g, '')}`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generateShareText());
      toast.success('Caption copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const shareToTwitter = () => {
    const text = encodeURIComponent(generateShareText());
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
  };

  const shareToWhatsApp = () => {
    const text = encodeURIComponent(generateShareText());
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Packing for ${destination}`,
          text: generateShareText(),
          url: 'https://odyvoy.com'
        });
      } catch (error) {
        // User cancelled or error
      }
    } else {
      copyToClipboard();
    }
  };

  const downloadCard = async () => {
    setIsGenerating(true);
    
    // Create a canvas-based image for download
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setIsGenerating(false);
      return;
    }

    // Card dimensions (Instagram story size)
    canvas.width = 1080;
    canvas.height = 1920;

    // Background gradient based on trip type
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    const hexColor = theme.hex;
    gradient.addColorStop(0, hexColor);
    gradient.addColorStop(1, adjustColor(hexColor, -30));
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Card background
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.beginPath();
    ctx.roundRect(80, 200, canvas.width - 160, 1520, 40);
    ctx.fill();

    // Text styling
    ctx.fillStyle = '#18181b';
    ctx.textAlign = 'center';

    // Destination
    ctx.font = 'bold 72px system-ui, sans-serif';
    ctx.fillText(destination, canvas.width / 2, 400);

    // Country
    ctx.font = '36px system-ui, sans-serif';
    ctx.fillStyle = '#71717a';
    ctx.fillText(country, canvas.width / 2, 470);

    // Trip details
    ctx.font = '32px system-ui, sans-serif';
    ctx.fillStyle = '#52525b';
    ctx.fillText(`${tripLength} days • ${tripType}`, canvas.width / 2, 560);

    // Progress circle
    const centerX = canvas.width / 2;
    const centerY = 850;
    const radius = 180;
    
    // Background circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.strokeStyle = '#e4e4e7';
    ctx.lineWidth = 24;
    ctx.stroke();

    // Progress arc
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, -Math.PI / 2, -Math.PI / 2 + (Math.PI * 2 * packedPercentage / 100));
    ctx.strokeStyle = theme.hex;
    ctx.lineWidth = 24;
    ctx.lineCap = 'round';
    ctx.stroke();

    // Percentage text
    ctx.fillStyle = '#18181b';
    ctx.font = 'bold 96px system-ui, sans-serif';
    ctx.fillText(`${Math.round(packedPercentage)}%`, centerX, centerY + 15);
    
    ctx.font = '32px system-ui, sans-serif';
    ctx.fillStyle = '#71717a';
    ctx.fillText('packed', centerX, centerY + 70);

    // Stats
    ctx.font = '36px system-ui, sans-serif';
    ctx.fillStyle = '#52525b';
    ctx.fillText(`${packedItems}/${totalItems} items`, centerX, 1150);
    ctx.fillText(`${packedEssentials}/${essentialItems} essentials`, centerX, 1210);

    // Branding
    ctx.font = 'bold 48px system-ui, sans-serif';
    ctx.fillStyle = theme.hex;
    ctx.fillText('OdyVoy', centerX, 1500);
    
    ctx.font = '28px system-ui, sans-serif';
    ctx.fillStyle = '#71717a';
    ctx.fillText('Your AI Travel Companion', centerX, 1560);
    ctx.fillText('odyvoy.com', centerX, 1620);

    // Download
    const link = document.createElement('a');
    link.download = `packing-card-${destination.toLowerCase().replace(/\s+/g, '-')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    
    setIsGenerating(false);
    toast.success('Card downloaded!');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[85vw] max-w-[320px] sm:max-w-sm max-h-[90vh] overflow-y-auto overflow-x-hidden p-3 sm:p-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Share Your Packing Progress
          </DialogTitle>
          <DialogDescription>
            Create a beautiful card to share on social media
          </DialogDescription>
        </DialogHeader>

        {/* Preview Card */}
        <div ref={cardRef} className="relative overflow-hidden rounded-xl">
          {/* Themed gradient header */}
          <div className={`h-3 w-full bg-gradient-to-r ${theme.gradient}`} />
          <Card className="border-0 border-t-0 rounded-t-none bg-card border-2 border-t-0 border-border">
            <CardContent className="p-4 space-y-3">
              {/* Header */}
              <div className="text-center space-y-1">
                <div className="flex items-center justify-center gap-2">
                  <MapPin className={`h-4 w-4 flex-shrink-0 ${theme.ring}`} />
                  <h3 className="text-lg font-bold truncate">{destination}</h3>
                </div>
                <p className="text-sm text-muted-foreground">{country}</p>
              </div>

              {/* Trip Info */}
              <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3 flex-shrink-0" />
                  <span>{tripLength} days</span>
                </div>
                <Badge className={`text-xs text-white ${theme.accent}`}>
                  {tripType}
                </Badge>
              </div>

              {/* Progress Ring */}
              <div className="flex justify-center py-2">
                <div className="relative h-20 w-20">
                  <svg className="h-full w-full -rotate-90" viewBox="0 0 128 128">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className="text-muted/30"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${(packedPercentage / 100) * 352} 352`}
                      className={`${theme.ring} transition-all duration-500`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-xl font-bold">{Math.round(packedPercentage)}%</span>
                    <span className="text-xs text-muted-foreground">packed</span>
                  </div>
                </div>
              </div>

              {/* Stats - Stacked Vertically */}
              <div className="flex flex-col items-center gap-0.5 text-center text-sm">
                <div className="flex items-center gap-1">
                  <span className="font-semibold">{packedItems}/{totalItems}</span>
                  <span className="text-muted-foreground text-xs">items packed</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="font-semibold">{packedEssentials}/{essentialItems}</span>
                  <span className="text-muted-foreground text-xs">essentials</span>
                </div>
              </div>

              {/* Branding - Packed by OdyVoy Badge */}
              <div className="text-center pt-2 border-t border-border">
                <a 
                  href="https://odyvoy.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${theme.ring} bg-current/10 text-sm font-medium hover:bg-current/20 transition-colors`}
                  style={{ backgroundColor: `${theme.hex}15` }}
                >
                  <Plane className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>Packed by OdyVoy</span>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="flex-col gap-2 w-full pt-2">
          <Button onClick={downloadCard} variant="outline" className="w-full h-9 text-sm" disabled={isGenerating}>
            <Download className="h-4 w-4 mr-2" />
            {isGenerating ? 'Generating...' : 'Download'}
          </Button>
          <Button onClick={shareToTwitter} className="w-full h-9 text-sm">
            Share on X
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
